'use client';

import { fetchMarketingStyleCategoryProductList } from '@/api/marketingPages';
import type { MarketingProductListResponse } from '@/app/(activity)/campaigns/[slug]/types';
import { serializeKey } from '@/utils/swr/keys';
import { useMemo } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import type { StyleHotProductRawAdCategory } from '../types';
import {
  STYLE_CATEGORY_PRODUCT_PAGE_SIZE,
  getMarketingProductListItems,
  hasLoadedStyleCategoryProducts,
  mergeStyleCategoryTabProducts,
} from '../utils';

interface UseStyleCategoryProductsParams {
  data?: StyleHotProductRawAdCategory | null;
  activeTabIndex: number;
  marketingPageId?: number;
  adp?: string;
  isPreview?: boolean;
}

export function useStyleCategoryProducts({
  data,
  activeTabIndex,
  marketingPageId,
  adp,
  isPreview = false,
}: UseStyleCategoryProductsParams) {
  const initialTabs = useMemo(() => data?.tabs || [], [data?.tabs]);
  const activeTab = initialTabs[activeTabIndex];
  const hasInitialProducts = hasLoadedStyleCategoryProducts(activeTab);
  const requestKey = useMemo(
    () =>
      marketingPageId && activeTab
        ? serializeKey([
            'styleHotProduct',
            'styleCategoryProducts',
            {
              marketingPageId,
              itemIndex: activeTabIndex,
              page: 1,
              pageSize: STYLE_CATEGORY_PRODUCT_PAGE_SIZE,
              appendCorrelation: 1,
              adp: adp || '',
              isPreview,
            },
          ])
        : null,
    [activeTab, activeTabIndex, adp, isPreview, marketingPageId],
  );
  const fallbackData = useMemo<MarketingProductListResponse | undefined>(
    () => (hasInitialProducts ? { items: activeTab?.products || [] } : undefined),
    [activeTab?.products, hasInitialProducts],
  );
  const { cache } = useSWRConfig();
  const hasCachedProducts = Boolean(requestKey && cache.get(requestKey)?.data !== undefined);
  const { data: response, isLoading } = useSWR<MarketingProductListResponse>(
    requestKey,
    () =>
      fetchMarketingStyleCategoryProductList({
        pageId: marketingPageId!,
        itemIndex: activeTabIndex,
        page: 1,
        pageSize: STYLE_CATEGORY_PRODUCT_PAGE_SIZE,
        appendCorrelation: 1,
        adp,
        isPreview,
      }),
    {
      fallbackData,
      revalidateOnMount: !hasInitialProducts && !hasCachedProducts,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      keepPreviousData: false,
    },
  );
  const tabs = useMemo(() => {
    if (!activeTab || !response) {
      return initialTabs;
    }
    // 切换风格爆品tab时，获取商品列表
    const productList = getMarketingProductListItems(response);

    return initialTabs.map((tab, index) =>
      index === activeTabIndex ? mergeStyleCategoryTabProducts(tab, productList) : tab,
    );
  }, [activeTab, activeTabIndex, initialTabs, response]);

  return {
    tabs,
    isActiveTabLoading: Boolean(requestKey && !response && isLoading),
  };
}
